<script setup>
import { computed, inject, onMounted } from 'vue';
import {
    Button,
    Input,
    InputNumber,
    Select,
    SelectOption,
    Empty,
    Spin
} from 'ant-design-vue';
import { CheckCircleOutlined, ArrowLeftOutlined } from '@ant-design/icons-vue';
import VersionEditorKpiRow from '@/components/consultation/VersionEditorKpiRow.vue';
import ConsultantVersionItemTable from '@/components/consultation/ConsultantVersionItemTable.vue';
import { formatAmountCompactWithDash } from '@/utils/balanceSheetDisplay';

const ve = inject('versionEditor', null);
const showConclusion = ve?.showConclusion;

const handleWorkflowClick = (step, index) => {
    console.log('[CW workflow click]', {
        key: step.key,
        index,
        hasVersionId: Boolean(step.data?.id),
        disabled: !step.data?.id && step.key !== 'conclusion',
        route: window.location.href
    });
    if (step.key === 'conclusion') {
        if (!showConclusion) {
            console.warn('[CW conclusion state] showConclusion ref is missing');
            return;
        }
        showConclusion.value = true;
        console.log('[CW conclusion state]', { value: showConclusion.value });
        return;
    }
    ve?.changeVersionWorkflowStep(index);
};

const versionInfoForm = ve?.versionInfoForm;
const udvForm = ve?.udvForm;

const allAssetRows = computed(() => (ve ? ve.assetRows.value || [] : []));
const allLiabilityRows = computed(() => (ve ? ve.liabilityRows.value || [] : []));

onMounted(() => {
    window.dispatchEvent(new CustomEvent('collapse-sidebar'));
});
</script>

<template>
    <div v-if="!ve" class="cw-compact-error">無法載入編輯器，請重新整理頁面。</div>
    <Spin v-else :spinning="!!ve.loadingSummary?.value">
        <div class="cw-compact">
            <nav v-if="ve.versionWorkflowSteps?.value?.length" class="cw-workflow-nav" aria-label="諮詢流程">
                <button
                    v-for="(step, index) in ve.versionWorkflowSteps.value"
                    :key="step.key"
                    type="button"
                    :class="['cw-workflow-step', { current: index === ve.versionWorkflowCurrent.value, locked: step.data?.locked || step.key === 'original' }]"
                    :disabled="!step.data?.id && step.key !== 'conclusion'"
                    @click="handleWorkflowClick(step, index)"
                >
                    <span class="cw-workflow-index">{{ index + 1 }}</span>
                    <span><b>{{ step.title }}</b><small>{{ step.subtitle }}</small></span>
                </button>
            </nav>
            <div class="cw-compact__toolbar">
                <Button type="link" class="cw-compact__back" @click="ve.goBack()">
                    <ArrowLeftOutlined /> 返回
                </Button>
                <div
                    v-if="!ve.isReadonly.value"
                    class="cw-compact__toolbar-actions"
                >
                    <template v-if="ve.isConsultationMode.value">
                        <Button
                            v-if="!ve.isVersionFinished?.value"
                            size="large"
                            :loading="ve?.finishLoading?.value"
                            @click="ve.showConsultationFinishVersionConfirm()"
                        >
                            完成版本
                        </Button>
                        <Button
                            type="primary"
                            size="large"
                            :loading="ve?.finishLoading?.value"
                            @click="ve.showConsultationSaveConfirm()"
                        >
                            <CheckCircleOutlined />
                            儲存
                        </Button>
                    </template>
                    <Button
                        v-else
                        type="primary"
                        size="large"
                        :loading="ve?.finishLoading?.value"
                        @click="ve.showFinishConfirm()"
                    >
                        <CheckCircleOutlined />
                        完成資產負債表
                    </Button>
                </div>
            </div>

            <div class="cw-compact__meta">
                <div class="cw-field cw-field--name">
                    <label>版本名稱</label>
                    <Input
                        v-model:value="versionInfoForm.external_name"
                        :disabled="ve.isReadonly.value"
                        placeholder="學員看到的版本名稱"
                        @blur="ve.flushConsultantMeta()"
                    />
                </div>
                <div class="cw-field">
                    <label>年齡</label>
                    <InputNumber
                        v-model:value="udvForm.age"
                        :min="0"
                        :disabled="ve.isReadonly.value || ve.udvLoading.value"
                        @blur="ve.flushConsultantMeta()"
                    />
                </div>
                <div class="cw-field">
                    <label>職業</label>
                    <Input
                        v-model:value="udvForm.job"
                        :disabled="ve.isReadonly.value || ve.udvLoading.value"
                        @blur="ve.flushConsultantMeta()"
                    />
                </div>
                <div class="cw-field">
                    <label>婚姻</label>
                    <Select
                        v-model:value="udvForm.marital_status"
                        :disabled="ve.isReadonly.value || ve.udvLoading.value"
                        @change="ve.flushConsultantMeta()"
                    >
                        <SelectOption :value="0">未婚</SelectOption>
                        <SelectOption :value="1">已婚</SelectOption>
                    </Select>
                </div>
                <div class="cw-field">
                    <label>子女</label>
                    <InputNumber
                        v-model:value="udvForm.children"
                        :min="0"
                        :disabled="ve.isReadonly.value || ve.udvLoading.value"
                        @blur="ve.flushConsultantMeta()"
                    />
                </div>
            </div>

            <VersionEditorKpiRow
                :totals="ve.summaryTotals.value"
                :format-amount="formatAmountCompactWithDash"
            />

            <div class="cw-compact__columns">
                <section class="cw-col cw-col--asset">
                    <div class="cw-col__head">
                        <h3>資產</h3>
                        <Button
                            v-if="!ve.isReadonly.value"
                            type="primary"
                            size="small"
                            @click="ve.openAddAsset()"
                        >
                            新增
                        </Button>
                    </div>
                    <ConsultantVersionItemTable
                        v-if="allAssetRows.length"
                        :class-type="0"
                        :rows="allAssetRows"
                        :ve="ve"
                        :is-readonly="ve.isReadonly.value"
                        amount-label="現值"
                        cash-flow-label="月收入"
                    />
                    <Empty v-else description="尚無資產項目" />
                </section>

                <section class="cw-col cw-col--liability">
                    <div class="cw-col__head">
                        <h3>負債</h3>
                        <Button
                            v-if="!ve.isReadonly.value"
                            type="primary"
                            size="small"
                            danger
                            @click="ve.openAddLiability()"
                        >
                            新增
                        </Button>
                    </div>
                    <ConsultantVersionItemTable
                        v-if="allLiabilityRows.length"
                        :class-type="1"
                        :rows="allLiabilityRows"
                        :ve="ve"
                        :is-readonly="ve.isReadonly.value"
                        amount-label="餘額"
                        cash-flow-label="月付"
                    />
                    <Empty v-else description="尚無負債項目" />
                </section>
            </div>
        </div>
    </Spin>
</template>

<style scoped>
.cw-compact {
    display: flex;
    flex-direction: column;
    gap: 16px;
    max-width: 100%;
    font-size: 15px;
}
.cw-workflow-nav{display:flex;gap:8px;padding:10px 12px;border:1px solid #e1d5b8;border-radius:10px;background:#fffaf0}.cw-workflow-step{flex:1;display:flex;align-items:center;justify-content:center;gap:8px;padding:7px 9px;border:1px solid transparent;border-radius:7px;background:transparent;color:#756348;cursor:pointer}.cw-workflow-step:hover{background:#f7edda}.cw-workflow-step.current{border-color:#b88a35;background:#f3e4c2;color:#67450f;box-shadow:inset 0 -3px #b88a35}.cw-workflow-step:disabled{opacity:.55;cursor:not-allowed}.cw-workflow-index{width:24px;height:24px;display:grid;place-items:center;border:2px solid currentColor;border-radius:50%;font-weight:700}.cw-workflow-step b,.cw-workflow-step small{display:block;text-align:left}.cw-workflow-step small{font-size:11px;color:#9b896c}@media(max-width:700px){.cw-workflow-nav{overflow-x:auto}.cw-workflow-step{min-width:120px}}
.cw-compact-error {
    padding: 24px;
    color: #8c8f96;
    text-align: center;
}
.cw-compact__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}
.cw-compact__back {
    padding-left: 0;
}
.cw-compact__toolbar-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    margin-left: auto;
}
.cw-compact__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 12px 16px;
    align-items: flex-end;
    padding: 12px 14px;
    background: #f8faf9;
    border: 1px solid #e8ecf0;
    border-radius: 10px;
}
.cw-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 100px;
}
.cw-field--name {
    flex: 1 1 220px;
    min-width: 200px;
}
.cw-field label {
    font-size: 13px;
    color: #8c8f96;
    font-weight: 500;
}

.cw-compact :deep(.ant-input),
.cw-compact :deep(.ant-input-number-input),
.cw-compact :deep(.ant-select-selector),
.cw-compact :deep(.ant-select-selection-item) {
    font-size: 15px !important;
}
.cw-compact__columns {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    align-items: start;
}
@media (max-width: 1200px) {
    .cw-compact__columns {
        grid-template-columns: 1fr;
    }
}
.cw-col {
    border: 1px solid #e8ecf0;
    border-radius: 10px;
    padding: 12px;
    background: #fff;
    min-height: 280px;
    min-width: 0;
}
.cw-col--asset {
    border-top: 3px solid #52c41a;
}
.cw-col--liability {
    border-top: 3px solid #ff4d4f;
}
.cw-col__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
}
.cw-col__head h3 {
    margin: 0;
    font-size: 17px;
    font-weight: 600;
    color: #272b36;
}
</style>
